/**
 * MD3 money and percent fields (FND-15: I4, I9) on the shared mask engine and TextField (I1).
 */

import {
  normalizeDigits,
  parseRatePercentInput,
  parseTomanInput,
  PERSIAN_DECIMAL_SEPARATOR,
  rateToPercentInput,
  type Rials,
  rialsToTomanInput,
  TOMAN_AMOUNT_MASK,
  tomanToWords,
  toPersianDigits,
} from '@saay/formatting';
import { useEffect, useMemo, useState } from 'react';

import { TextField, type TextFieldProps, type TextFieldVariant } from './text-fields.js';

// Owner (2026-10-28): the amount in words under every money field, from one thousand toman.
const TOMAN_WORDS_MIN = 1_000;

const MONEY_INPUT_MESSAGES = {
  INVALID: 'مبلغ را فقط با رقم وارد کنید.',
  TOO_LARGE: 'مبلغ بیش از حد مجاز است.',
  TOO_MANY_DECIMALS: 'مبلغ تومان حداکثر یک رقم اعشار دارد.',
} as const;

function maskRawToRials(raw: string): ReturnType<typeof parseTomanInput> {
  if (raw === '') {
    return { error: 'EMPTY', ok: false };
  }
  const display = TOMAN_AMOUNT_MASK.formatDigits(raw);
  return parseTomanInput(display);
}

function rialsToMaskRaw(rials: Rials | null): string {
  if (rials === null) {
    return '';
  }
  const text = rialsToTomanInput(rials);
  return TOMAN_AMOUNT_MASK.extractDigits(text);
}

function wholeTomanFromRials(rials: Rials): number {
  return rials / 10;
}

function moneyWordsHint(raw: string, enabled: boolean): string | undefined {
  if (!enabled || raw === '') {
    return undefined;
  }
  const parsed = maskRawToRials(raw);
  if (!parsed.ok) {
    return undefined;
  }
  const wholeToman = wholeTomanFromRials(parsed.rials);
  if (wholeToman < TOMAN_WORDS_MIN || parsed.rials % 10 !== 0) {
    return undefined;
  }
  return tomanToWords(wholeToman);
}

export interface MoneyFieldProps extends Omit<
  TextFieldProps,
  'mask' | 'maskRaw' | 'onMaskRawChange' | 'onChange' | 'suffix' | 'value'
> {
  /** When false, amount-in-words supporting text is omitted (default true). */
  readonly amountInWords?: boolean;
  readonly maxRials?: Rials;
  readonly minRials?: Rials;
  readonly onChangeRials: (rials: Rials | null) => void;
  /**
   * Whether the typed text is not a valid amount. `onChangeRials(null)` means both "empty" and
   * "invalid", so a form where empty has a meaning (a default amount) needs this to tell them apart.
   */
  readonly onInvalidChange?: (invalid: boolean) => void;
  readonly valueRials: Rials | null;
  readonly variant?: TextFieldVariant;
}

/** Toman input with live grouping, optional amount in words from one million toman (I4, Q5). */
export function MoneyField({
  amountInWords = true,
  error,
  hint,
  maxRials,
  minRials,
  onChangeRials,
  onInvalidChange,
  onBlur,
  valueRials,
  ...rest
}: MoneyFieldProps) {
  const [raw, setRaw] = useState(() => rialsToMaskRaw(valueRials));
  const [parseError, setParseError] = useState<string | undefined>(undefined);
  const [boundsError, setBoundsError] = useState<string | undefined>(undefined);

  useEffect(() => {
    // An invalid entry reports `null`; keep the typed text (and its error) instead of clearing it.
    setRaw((current) =>
      valueRials === null && current !== '' && !maskRawToRials(current).ok
        ? current
        : rialsToMaskRaw(valueRials),
    );
  }, [valueRials]);

  const wordsHint = useMemo(() => moneyWordsHint(raw, amountInWords), [amountInWords, raw]);

  function commitRaw(nextRaw: string): void {
    setRaw(nextRaw);
    const parsed = maskRawToRials(nextRaw);
    if (parsed.ok) {
      setParseError(undefined);
      onInvalidChange?.(false);
      onChangeRials(parsed.rials);
      return;
    }
    if (parsed.error === 'EMPTY') {
      setParseError(undefined);
      onInvalidChange?.(false);
      onChangeRials(null);
      return;
    }
    setParseError(MONEY_INPUT_MESSAGES[parsed.error]);
    onInvalidChange?.(true);
    onChangeRials(null);
  }

  function validateBounds(rials: Rials): string | undefined {
    if (minRials !== undefined && rials < minRials) {
      return `مبلغ باید حداقل ${rialsToTomanInput(minRials)} تومان باشد.`;
    }
    if (maxRials !== undefined && rials > maxRials) {
      return `مبلغ باید حداکثر ${rialsToTomanInput(maxRials)} تومان باشد.`;
    }
    return undefined;
  }

  const trailingValid =
    raw !== '' && parseError === undefined && boundsError === undefined && maskRawToRials(raw).ok;

  const supportHint =
    error === undefined && parseError === undefined && boundsError === undefined
      ? (wordsHint ?? hint)
      : hint;

  return (
    <TextField
      {...rest}
      autoComplete="off"
      error={boundsError ?? parseError ?? error}
      hint={supportHint}
      inputMode="decimal"
      ltr
      mask={TOMAN_AMOUNT_MASK}
      maskRaw={raw}
      onBlur={(event) => {
        const parsed = maskRawToRials(raw);
        if (parsed.ok) {
          setBoundsError(validateBounds(parsed.rials));
        } else {
          setBoundsError(undefined);
        }
        onBlur?.(event);
      }}
      onMaskRawChange={commitRaw}
      suffix="تومان"
      trailingValid={trailingValid === true}
    />
  );
}

const PERCENT_RANGE_ERROR = 'نرخ باید از ۰ تا کمتر از ۱۰۰ درصد باشد.';

function normalizePercentTyping(text: string): string {
  return normalizeDigits(text)
    .replace(/[%٪]/g, '')
    .replace(/[٫,]/g, '.')
    .replace(/[^\d.]/g, '')
    .replace(/(\..*)\./g, '$1');
}

export interface PercentFieldProps extends Omit<
  TextFieldProps,
  'mask' | 'maskRaw' | 'onMaskRawChange' | 'onChange' | 'suffix' | 'value' | 'ltr'
> {
  readonly onChangeRate: (rate: string | null) => void;
  /** API decimal rate (`0.080000` for 8%), or null when empty/invalid. */
  readonly valueRate: string | null;
}

/** Fee and policy rate field: percent in the UI, decimal rate string at the API (I9). */
export function PercentField({
  error,
  hint,
  onBlur,
  onChangeRate,
  valueRate,
  ...rest
}: PercentFieldProps) {
  const [text, setText] = useState(() => (valueRate === null ? '' : rateToPercentInput(valueRate)));
  const [parseError, setParseError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setText(valueRate === null ? '' : rateToPercentInput(valueRate));
  }, [valueRate]);

  function commit(nextText: string): void {
    const normalized = normalizePercentTyping(nextText);
    const fraction = normalized.split('.')[1] ?? '';
    if (fraction.length > 4) {
      return;
    }
    setText(normalized);
    if (normalized.trim() === '') {
      setParseError(undefined);
      onChangeRate(null);
      return;
    }
    const rate = parseRatePercentInput(normalized);
    if (rate === null) {
      setParseError(PERCENT_RANGE_ERROR);
      onChangeRate(null);
      return;
    }
    setParseError(undefined);
    onChangeRate(rate);
  }

  const trailingValid =
    text !== '' && parseError === undefined && parseRatePercentInput(text) !== null;

  return (
    <TextField
      {...rest}
      autoComplete="off"
      error={parseError ?? error}
      hint={hint}
      inputMode="decimal"
      ltr
      onBlur={(event) => {
        if (text.trim() !== '') {
          const rate = parseRatePercentInput(text);
          if (rate === null) {
            setParseError(PERCENT_RANGE_ERROR);
          } else {
            setText(rateToPercentInput(rate));
            setParseError(undefined);
          }
        }
        onBlur?.(event);
      }}
      onChange={(event) => commit(event.target.value)}
      suffix="٪"
      trailingValid={trailingValid}
      // Persian digits and decimal separator on screen; the typed text is normalized on change.
      value={toPersianDigits(text).replace('.', PERSIAN_DECIMAL_SEPARATOR)}
    />
  );
}
